import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Filter, Loader2, X, ChevronDown, ChevronRight, Search, Check, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DateRangeSelector from './DateRangeSelector';
import axios from 'axios';

// Type definitions
type PeriodData = {
  period: number;
  revenue: number;
  predicted_churn: number;
  count: number;
  predicted_revenue: number;
  calculated_value: number;
  text_formula: string;
  digital_formula: string;
};

type StatData = {
  period?: string;
  country: string;
  campaign: string;
  revenue_activation_count: number;
  revenue_activation_purchase_count: number;
  sum_activation_purchases: number;
  cost: number;
  cpa: number;
  cpu: number;
  ltv: number;
  predicted_ltv: number;
  roi: number;
  predicted_roi: number;
  total_revenue: number;
  total_revenue_activation_purchase: number;
  total_count_unique_payers?: number;
  total_count_unique_payers_activation?: number;
  profit: number;
  vat_applied: boolean;
  periods: PeriodData[];
  
};

type TotalStatistics = {
  revenue_activation_count: number;
  revenue_activation_purchase_count: number;
  sum_activation_purchases: number;
  cost: number;
  cpa: number;
  cpu: number;
  ltv: number;
  predicted_ltv: number;
  roi: number;
  predicted_roi: number;
  total_count_unique_payers: number;
  total_count_unique_payers_activation: number;
  total_revenue: number;
  total_revenue_activation_purchase: number;
  profit: number;
  vat_applied: boolean;
  periods: PeriodData[];
  period?: string;
  country?: string;
  
};

type CountryStatistics = Record<string, TotalStatistics>;

type ApiResponse = {
  success: boolean;
  count: number;
  view: string;
  period?: string;
  vat_applied: boolean;
  data: StatData[];
  total_statistics: TotalStatistics;
  country_statistics: Record<string, TotalStatistics>;
};

// VAT rates type
type VatRates = {
  [country: string]: number;
};

// Icons for toggle buttons
const CheckIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const CrossIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const DailyStatsDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  // State for UI controls
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true);
  
  // Display options
  const [showCountries, setShowCountries] = useState<boolean>(true);
  const [showCampaigns, setShowCampaigns] = useState<boolean>(true);
  const [showWithTax, setShowWithTax] = useState<boolean>(true);
  
  // Filter states
  const [countries, setCountries] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  
  // Expanded states for hierarchical view
  const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});
  
  // VAT rates storage
  const [vatRates, setVatRates] = useState<VatRates>({});
  
  // View options
  const [viewMode, setViewMode] = useState<'daily' | 'month'>('daily');
  const [useTotalView, setUseTotalView] = useState<boolean>(true);
  
  // Data state
  const [dashboardData, setDashboardData] = useState<ApiResponse | null>(null);
  
  // Modal states
  const [isCountryModalOpen, setIsCountryModalOpen] = useState<boolean>(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState<boolean>(false);
  const [tempSelectedCountries, setTempSelectedCountries] = useState<string[]>([]);
  const [tempSelectedCampaigns, setTempSelectedCampaigns] = useState<string[]>([]);
  
  // Detail view modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [selectedDetail, setSelectedDetail] = useState<{
    title: string;
    data: StatData | TotalStatistics;
    country?: string;
  } | null>(null);
  
  // Search in dropdowns
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [campaignSearch, setCampaignSearch] = useState<string>('');
  
  // Date range
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [dateFrom, setDateFrom] = useState<string>(getTodayDate());
  const [dateTo, setDateTo] = useState<string>(getTodayDate());
  
  // Refs for modals
  const filtersRef = useRef<HTMLDivElement>(null);
  const countryModalRef = useRef<HTMLDivElement>(null);
  const campaignModalRef = useRef<HTMLDivElement>(null);
  const detailModalRef = useRef<HTMLDivElement>(null);
  
  const calculateTotalRevenue = (periods?: PeriodData[]): number => {
    if (!periods || periods.length === 0) return 0;
  
    return periods.reduce((total, period) => {
      // Use calculated_value or revenue, defaulting to 0 if undefined
      return total + (period.calculated_value || period.revenue || 0);
    }, 0);
  };

  // Functions for VAT handling
  const getVatRate = (country: string): number => {
    return vatRates[country] !== undefined ? vatRates[country] : 0.0;
  };
  
  const getAdjustedRevenue = (value: number | undefined, country: string): number => {
    if (value === undefined || value === null) return 0;
    if (!showWithTax) return value;
    
    const vatRate = getVatRate(country);
    return value * (1 - vatRate);
  };
  
  // Toggle expanded state for country
  const toggleCountry = (country: string) => {
    setExpandedCountries(prev => ({
      ...prev,
      [country]: !prev[country]
    }));
  };
  
  // Handle row click to show details
  const handleRowClick = (data: StatData | TotalStatistics, title: string, country: string = 'All') => {
    setSelectedDetail({ title, data, country });
    setIsDetailModalOpen(true);
  };
  
  // Fetch VAT rates on component mount
  useEffect(() => {
    const fetchVatRates = async () => {
      try {
        const response = await axios.get('https://dashboard-nk.golive.mobi/api/api/vat');
        if (response.data.success) {
          const rates: VatRates = {};
          response.data.data.forEach((item: { country: string, VAT: number }) => {
            rates[item.country] = item.VAT;
          });
          setVatRates(rates);
        }
      } catch (err) {
        console.error('Failed to fetch VAT rates:', err);
      }
    };
    
    fetchVatRates();
  }, []);
  
  // Fetch countries and campaigns
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Construct parameters for the API requests based on current filter state
        const params: Record<string, any> = {};
        
        // Add date filters
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        
        // Load list of countries
        const countriesResponse = await axios.get('https://dashboard-nk.golive.mobi/api/api/countries', { params });
        if (countriesResponse.data.success) {
          setCountries(countriesResponse.data.data);
        }
        
        // Add country filter for campaigns endpoint
        const campaignParams = { ...params };
        if (selectedCountries.length === 1) {
          campaignParams.country = selectedCountries[0];
        }
        
        // Load list of campaigns
        const campaignsResponse = await axios.get('https://dashboard-nk.golive.mobi/api/api/campaigns', { params: campaignParams });
        if (campaignsResponse.data.success) {
          setCampaigns(campaignsResponse.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch filter data:', err);
      }
    };
    
    fetchFilterData();
  }, [dateFrom, dateTo, selectedCountries]);
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const endpoint = 'https://dashboard-nk.golive.mobi/api/api/daily/stat';
        const params: Record<string, any> = {
          date_from: dateFrom,
          date_to: dateTo,
          view: viewMode,
          total: useTotalView,
          vat: showWithTax,
          hide_country: !showCountries,
          hide_campaign: !showCampaigns
        };
        
        // Add country filter if selected
        if (selectedCountries.length === 1) {
          params.country = selectedCountries[0];
        }
        
        // Add campaign filter if selected
        if (selectedCampaigns.length === 1) {
          params.campaign = selectedCampaigns[0];
        }
        
        const response = await axios.get(endpoint, { params });
        setDashboardData(response.data);
        
        // Initialize expanded state for all countries
        if (response.data.country_statistics) {
          const initialExpandedState: Record<string, boolean> = {};
          
          Object.keys(response.data.country_statistics).forEach(country => {
            initialExpandedState[country] = false;
          });
          
          setExpandedCountries(initialExpandedState);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to fetch data');
        setLoading(false);
      }
    };
    
    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);
    const daysDifference = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
    
    // Only fetch if date range is valid
    if (daysDifference >= 0) {
      fetchDashboardData();
    }
  }, [dateFrom, dateTo, viewMode, useTotalView, showWithTax, showCountries, showCampaigns, selectedCountries, selectedCampaigns]);
  
  // Initialize temporary selections when modals open
  useEffect(() => {
    if (isCountryModalOpen) {
      setTempSelectedCountries([...selectedCountries]);
    }
  }, [isCountryModalOpen, selectedCountries]);
  
  useEffect(() => {
    if (isCampaignModalOpen) {
      setTempSelectedCampaigns([...selectedCampaigns]);
    }
  }, [isCampaignModalOpen, selectedCampaigns]);
  
  // Close modals when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryModalRef.current && !countryModalRef.current.contains(event.target as Node)) {
        setIsCountryModalOpen(false);
      }
      if (campaignModalRef.current && !campaignModalRef.current.contains(event.target as Node)) {
        setIsCampaignModalOpen(false);
      }
      if (detailModalRef.current && !detailModalRef.current.contains(event.target as Node)) {
        setIsDetailModalOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle country selection
  const toggleAllCountries = () => {
    if (tempSelectedCountries.length === countries.length) {
      setTempSelectedCountries([]);
    } else {
      setTempSelectedCountries([...countries]);
    }
  };
  
  const toggleCountryInTemp = (country: string) => {
    setTempSelectedCountries(prev => 
      prev.includes(country) 
        ? prev.filter(c => c !== country) 
        : [...prev, country]
    );
  };
  
  const applyCountryFilter = () => {
    setSelectedCountries(tempSelectedCountries);
    setIsCountryModalOpen(false);
  };
  
  // Handle campaign selection
  const toggleAllCampaigns = () => {
    if (tempSelectedCampaigns.length === campaigns.length) {
      setTempSelectedCampaigns([]);
    } else {
      setTempSelectedCampaigns([...campaigns]);
    }
  };
  
  const toggleCampaignInTemp = (campaign: string) => {
    setTempSelectedCampaigns(prev => 
      prev.includes(campaign) 
        ? prev.filter(c => c !== campaign) 
        : [...prev, campaign]
    );
  };
  
  const applyCampaignFilter = () => {
    setSelectedCampaigns(tempSelectedCampaigns);
    setIsCampaignModalOpen(false);
  };
  
  // Filter campaigns and countries by search
  const filteredCountries = countries.filter(country => 
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );
  
  const filteredCampaigns = campaigns.filter(campaign => 
    campaign.toLowerCase().includes(campaignSearch.toLowerCase())
  );
  
  // Header with title and back button
  const Header: React.FC = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 30px',
      backgroundColor: 'white',
      borderRadius: '0 0 12px 12px',
      boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button 
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            marginRight: '15px',
            color: '#495057',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 600,
          color: '#2c3e50'
        }}>
          Daily Stats Dashboard
        </h1>
      </div>
      
      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: isFilterOpen ? '#e9ecef' : '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '8px 15px',
            color: '#495057',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>
    </div>
  );
  
  // Filters section
  const Filters: React.FC = () => {
    return (
      <div 
        ref={filtersRef} 
        className="filters-animation"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          margin: '20px 30px',
          padding: '15px 20px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
        }}
      >
        {/* Display toggles */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px',
          paddingBottom: '15px',
          borderBottom: '1px solid #e9ecef'
        }}>
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#2c3e50' }}>Show Countries</span>
              <button 
                onClick={() => setShowCountries(!showCountries)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {showCountries ? <CheckIcon /> : <CrossIcon />}
              </button>
            </div>
            
            <span style={{ color: 'lightgray' }}>|</span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#2c3e50' }}>Show Campaigns</span>
              <button 
                onClick={() => setShowCampaigns(!showCampaigns)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {showCampaigns ? <CheckIcon /> : <CrossIcon />}
              </button>
            </div>
            
            <span style={{ color: 'lightgray' }}>|</span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#2c3e50' }}>Show VAT</span>
              <button 
                onClick={() => setShowWithTax(!showWithTax)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {showWithTax ? <CheckIcon /> : <CrossIcon />}
              </button>
            </div>
            
            <span style={{ color: 'lightgray' }}>|</span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#2c3e50' }}>Use Total View</span>
              <button 
                onClick={() => setUseTotalView(!useTotalView)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {useTotalView ? <CheckIcon /> : <CrossIcon />}
              </button>
            </div>
            
            <span style={{ color: 'lightgray' }}>|</span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ color: '#2c3e50' }}>View Mode</span>
              <div style={{
                display: 'flex',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <button 
                  onClick={() => setViewMode('daily')}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    backgroundColor: viewMode === 'daily' ? '#3498db' : '#f8f9fa',
                    color: viewMode === 'daily' ? 'white' : '#495057',
                    cursor: 'pointer'
                  }}
                >
                  Daily
                </button>
                <button 
                  onClick={() => setViewMode('month')}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    backgroundColor: viewMode === 'month' ? '#3498db' : '#f8f9fa',
                    color: viewMode === 'month' ? 'white' : '#495057',
                    cursor: 'pointer'
                  }}
                >
                  Monthly
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Filter selectors */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          <div style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: '15px',
            marginBottom: '15px'
          }}>
            {/* Country selection */}
            <div style={{ minWidth: '220px' }}>
              <button 
                onClick={() => setIsCountryModalOpen(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '14px'
                }}
              >
                <span>{selectedCountries.length ? `${selectedCountries.length} countries selected` : 'Select countries'}</span>
                <ChevronDown size={16} />
              </button>
            </div>
            
            {/* Campaign selection */}
            <div style={{ minWidth: '220px' }}>
              <button 
                onClick={() => setIsCampaignModalOpen(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '14px'
                }}
              >
                <span>{selectedCampaigns.length ? `${selectedCampaigns.length} campaigns selected` : 'Select campaigns'}</span>
                <ChevronDown size={16} />
              </button>
            </div>

            {/* Date range selection */}
            <DateRangeSelector 
              onApply={(startDate, endDate, viewMode) => {
                setDateFrom(startDate);
                setDateTo(endDate || startDate);
                if (viewMode === 'month') {
                  setViewMode('month');
                } else {
                  setViewMode('daily');
                }
              }}
              initialStartDate={dateFrom}
              initialEndDate={dateTo}
              initialViewMode={viewMode === 'daily' ? 'day' : 'month'}
              placeholder="Select Period"
            />
          </div>
        </div>
      </div>
    );
  };

  // Country selection modal
  const CountryModal: React.FC = () => {
    if (!isCountryModalOpen) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div ref={countryModalRef} className="modal-content" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          padding: '20px',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>Select Countries</h3>
            <button
              onClick={() => setIsCountryModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6c757d'
              }}
            >
              <X size={20} />
            </button>
          </div>
          
          <div style={{ 
            position: 'relative',
            marginBottom: '15px'
          }}>
            <Search style={{ 
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }} size={16} />
            <input
              type="text"
              style={{
                padding: '10px 10px 10px 36px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                width: '100%',
                fontSize: '14px'
              }}
              placeholder="Search countries..."
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
            />
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '1px solid #e9ecef',
            cursor: 'pointer'
          }} onClick={toggleAllCountries}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              marginRight: '10px',
              backgroundColor: tempSelectedCountries.length === countries.length ? '#3498db' : 'transparent'
            }}>
              {tempSelectedCountries.length === countries.length && (
                <Check size={16} color="white" />
              )}
            </div>
            <span style={{ fontWeight: 500 }}>Select All</span>
          </div>
          
          <div style={{
            overflowY: 'auto',
            flex: 1,
            marginBottom: '15px'
          }}>
            {filteredCountries.length > 0 ? (
              filteredCountries.map(country => (
                <div 
                  key={country}
                  onClick={() => toggleCountryInTemp(country)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 0',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    marginRight: '10px',
                    backgroundColor: tempSelectedCountries.includes(country) ? '#3498db' : 'transparent'
                  }}>
                    {tempSelectedCountries.includes(country) && (
                      <Check size={16} color="white" />
                    )}
                  </div>
                  <span>{country}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: '15px 0', textAlign: 'center', color: '#6c757d' }}>
                No countries matching your search
              </div>
            )}
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            borderTop: '1px solid #e9ecef',
            paddingTop: '15px'
          }}>
            <button
              onClick={() => setIsCountryModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                color: '#495057'
              }}
            >
              Cancel
            </button>
            <button
              onClick={applyCountryFilter}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#3498db',
                cursor: 'pointer',
                color: 'white',
                fontWeight: 500
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Campaign selection modal
  const CampaignModal: React.FC = () => {
    if (!isCampaignModalOpen) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div ref={campaignModalRef} className="modal-content" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '80vh',
          padding: '20px',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>Select Campaigns</h3>
            <button
              onClick={() => setIsCampaignModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6c757d'
              }}
            >
              <X size={20} />
            </button>
          </div>
          
          <div style={{ 
            position: 'relative',
            marginBottom: '15px'
          }}>
            <Search style={{ 
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }} size={16} />
            <input
              type="text"
              style={{
                padding: '10px 10px 10px 36px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                width: '100%',
                fontSize: '14px'
              }}
              placeholder="Search campaigns..."
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
            />
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '1px solid #e9ecef',
            cursor: 'pointer'
          }} onClick={toggleAllCampaigns}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              marginRight: '10px',
              backgroundColor: tempSelectedCampaigns.length === campaigns.length ? '#3498db' : 'transparent'
            }}>
              {tempSelectedCampaigns.length === campaigns.length && (
                <Check size={16} color="white" />
              )}
            </div>
            <span style={{ fontWeight: 500 }}>Select All</span>
          </div>
          
          <div style={{
            overflowY: 'auto',
            flex: 1,
            marginBottom: '15px'
          }}>
            {filteredCampaigns.length > 0 ? (
              filteredCampaigns.map(campaign => (
                <div 
                  key={campaign}
                  onClick={() => toggleCampaignInTemp(campaign)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 0',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    marginRight: '10px',
                    backgroundColor: tempSelectedCampaigns.includes(campaign) ? '#3498db' : 'transparent'
                  }}>
                    {tempSelectedCampaigns.includes(campaign) && (
                      <Check size={16} color="white" />
                    )}
                  </div>
                  <span>{campaign}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: '15px 0', textAlign: 'center', color: '#6c757d' }}>
                No campaigns matching your search
              </div>
            )}
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            borderTop: '1px solid #e9ecef',
            paddingTop: '15px'
          }}>
            <button
              onClick={() => setIsCampaignModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                color: '#495057'
              }}
            >
              Cancel
            </button>
            <button
              onClick={applyCampaignFilter}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#3498db',
                cursor: 'pointer',
                color: 'white',
                fontWeight: 500
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Detail modal for showing period data
  const DetailModal: React.FC = () => {
    if (!isDetailModalOpen || !selectedDetail) return null;
    
    const { title, data, country = 'All' } = selectedDetail;
    
    // Collect data for periods
    const predictedValues: { 
      period: string; 
      revenue: { 
        actual: number | null; 
        predicted: number | null; 
        formula: string | null;
      };
      churn: { 
        actual: number | null; 
        predicted: number | null; 
      };
    }[] = [];
    
    // Build period data
    if (Array.isArray((data as any).periods)) {
      (data as any).periods.forEach((period: PeriodData) => {
        // Adjust revenue with VAT if needed
        const adjustedRevenueValue = getAdjustedRevenue(period.revenue, country);
        const adjustedPredictedRevenueValue = getAdjustedRevenue(period.predicted_revenue, country);
        
        // Add data to the array
        predictedValues.push({
          period: `P${period.period}`,
          revenue: {
            actual: adjustedRevenueValue,
            predicted: adjustedPredictedRevenueValue,
            formula: period.text_formula || null
          },
          churn: {
            actual: period.predicted_churn || null,
            predicted: period.predicted_churn || null
          }
        });
      });
    }
    
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setIsDetailModalOpen(false);
          }
        }}
      >
        <div 
          ref={detailModalRef}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1400px',
            maxHeight: '90vh',
            padding: '20px',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
          }}
        >
          {/* Modal header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: '1px solid #e9ecef',
            paddingBottom: '10px'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>
              {title}
              {showWithTax && (
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: 'normal', 
                  marginLeft: '10px', 
                  color: '#6c757d' 
                }}>
                  After VAT {country !== 'All' ? `(${getVatRate(country) * 100}%)` : ''}
                </span>
              )}
            </h3>
            <button
              onClick={() => setIsDetailModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6c757d'
              }}
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Summary metrics */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '10px',
            marginBottom: '20px'
          }}>
            <div style={{
              backgroundColor: '#f0f8ff',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Total Revenue</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                {getAdjustedRevenue(data.total_revenue, country).toFixed(2)}
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#f0f8ff',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Total Revenue Activation</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                {getAdjustedRevenue(data.total_revenue_activation_purchase || 0, country).toFixed(2)}
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#f0fff0',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>LTV</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                {(data.ltv || 0).toFixed(2)}
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#f0fff0',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>Predicted LTV</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '5px' }}>
                {(data.predicted_ltv || 0).toFixed(2)}
              </div>
            </div>
          </div>
          
          <h4 style={{ margin: '20px 0 10px', color: '#2c3e50' }}>Detailed Period Breakdown</h4>
          
          {/* Period table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    borderBottom: '1px solid #e9ecef'
                  }}>Period</th>
                  <th style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    borderBottom: '1px solid #e9ecef'
                  }}>Actual Revenue</th>
                  <th style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    borderBottom: '1px solid #e9ecef'
                  }}>Predicted Revenue</th>
                  <th style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    borderBottom: '1px solid #e9ecef'
                  }}>Predicted Churn</th>
                  <th style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    borderBottom: '1px solid #e9ecef',
                    width: '40%'
                  }}>Formula</th>
                </tr>
              </thead>
              <tbody>
                {predictedValues.map(({ period, revenue, churn }) => (
                  <tr key={period}>
                    <td style={{ 
                      padding: '10px',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: '1px solid #e9ecef'
                    }}>{period}</td>
                    <td style={{ 
                      padding: '10px',
                      textAlign: 'right',
                      borderBottom: '1px solid #e9ecef'
                    }}>{revenue.actual !== null ? revenue.actual.toFixed(2) : '—'}</td>
                    <td style={{ 
                      padding: '10px',
                      textAlign: 'right',
                      borderBottom: '1px solid #e9ecef'
                    }}>{revenue.predicted !== null ? revenue.predicted.toFixed(2) : '—'}</td>
                    <td style={{ 
                      padding: '10px',
                      textAlign: 'right',
                      borderBottom: '1px solid #e9ecef'
                    }}>{churn.predicted !== null ? churn.predicted.toFixed(4) : '—'}</td>
                    <td style={{ 
                      padding: '8px 10px',
                      textAlign: 'left',
                      borderBottom: '1px solid #e9ecef',
                      fontSize: '12px',
                      color: '#6c757d',
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f9fa'
                    }}>{revenue.formula || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };
  
  // Loading component
  const Loader: React.FC = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px'
    }}>
      <Loader2 
        size={48} 
        style={{ 
          animation: 'spin 1s linear infinite',
          color: '#3498db'
        }} 
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .filters-animation {
          animation: slideDown 0.3s ease forwards;
        }

        .modal-overlay {
          animation: fadeIn 0.2s ease-out;
        }
        
        .modal-content {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
  
  // Main data table component
  const StatisticsTable: React.FC = () => {


    
    if (!dashboardData) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>No data available</div>;
    }
    
    // Define styles
    const cellStyle = {
      padding: '12px 15px',
      textAlign: 'right' as const,
      color: '#495057',
      borderBottom: '1px solid #e9ecef'
    };

    const headerStyle = {
      padding: '15px 15px',
      textAlign: 'right' as const,
      fontWeight: 600 as const,
      color: '#6c757d',
      fontSize: '13px',
      textTransform: 'uppercase' as const,
      borderBottom: '2px solid #dee2e6',
      backgroundColor: '#f8f9fa'
    };
    
    // Entity column style
    const entityCellStyle = {
      padding: '12px 15px',
      textAlign: 'left' as const,
      color: '#495057',
      borderBottom: '1px solid #e9ecef'
    };
    
    // Total row style
    const totalRowStyle = {
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold' as const
    };
    
    // Country row style
    const countryRowStyle = {
      backgroundColor: '#fafafa',
      fontWeight: 600 as const
    };
    
    // Campaign row style with indent
    const campaignRowStyle = {
      ...entityCellStyle,
      paddingLeft: '40px'
    };
    
    // Initialize table rows array
    const tableRows: React.ReactNode[] = [];
    
    // Add total row if available
    if (dashboardData.total_statistics) {
      const totalStats = dashboardData.total_statistics;
      
      tableRows.push(
        <tr key="total" style={totalRowStyle}>
          <td 
            style={{
              ...entityCellStyle,
              fontWeight: 700,
              borderBottom: '2px solid #dee2e6'
            }}
          >
            TOTAL ({dashboardData.period || (viewMode === 'daily' ? 'Daily' : 'Monthly')})
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {totalStats.revenue_activation_count || 0}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {totalStats.revenue_activation_purchase_count || 0}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {totalStats.sum_activation_purchases || (totalStats.revenue_activation_count + totalStats.revenue_activation_purchase_count)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.cost || 0).toFixed(2)}
          </td>
          <td style={{
  ...cellStyle,
  backgroundColor: '#e9f7fe',
  fontWeight: 700,
  borderBottom: '2px solid #dee2e6'
}}>
  {getAdjustedRevenue(
    calculateTotalRevenue(totalStats.periods),
    'All'
  ).toFixed(2)}
</td>
          
          {/* P1-P12 values for total */}
          {Array.from({ length: 12 }, (_, i) => {
            const periodNum = i + 1;
            const periodKey = `revenue_p${periodNum}`;
            let value = 0;
            
            // Try to get period value from periods array if it exists
            if (Array.isArray(totalStats.periods)) {
              const periodData = totalStats.periods.find(p => p.period === periodNum);
              if (periodData) {
                value = periodData.revenue;
              }
            }
            
            return (
              <td key={`total-p${periodNum}`} style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
                {getAdjustedRevenue(value, 'All').toFixed(2)}
              </td>
            );
          })}
          
          <td style={{
            ...cellStyle,
            backgroundColor: '#e9f7fe',
            fontWeight: 700,
            borderBottom: '2px solid #dee2e6'
          }}>
            {getAdjustedRevenue(totalStats.total_revenue || 0, 'All').toFixed(2)}
          </td>
          
          <td style={{
            ...cellStyle,
            backgroundColor: '#f0fff0',
            fontWeight: 700,
            borderBottom: '2px solid #dee2e6'
          }}>
            {(totalStats.profit || getAdjustedRevenue(totalStats.total_revenue || 0, 'All') - (totalStats.cost || 0)).toFixed(2)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.cpa || 0).toFixed(2)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.cpu || 0).toFixed(2)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.ltv || 0).toFixed(2)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.predicted_ltv || 0).toFixed(2)}
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.roi || 0).toFixed(2)}%
          </td>
          <td style={{...cellStyle, borderBottom: '2px solid #dee2e6'}}>
            {(totalStats.predicted_roi || 0).toFixed(2)}%
          </td>
        </tr>
      );
    }
    
    // Add country rows and campaign rows
    if (showCountries && dashboardData.country_statistics) {
      // For each country
      Object.entries(dashboardData.country_statistics).forEach(([countryCode, countryStats]) => {
        // Add country row with toggle
        tableRows.push(
          <tr key={`country-${countryCode}`} style={countryRowStyle}>
            <td 
              style={{
                ...entityCellStyle,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={() => toggleCountry(countryCode)}
            >
              {expandedCountries[countryCode] ? 
                <ChevronDown size={16} /> : 
                <ChevronRight size={16} />
              }
              <span>{countryCode}</span>
            </td>
            <td style={cellStyle}>
              {countryStats.revenue_activation_count || 0}
            </td>
            <td style={cellStyle}>
              {countryStats.revenue_activation_purchase_count || 0}
            </td>
            <td style={cellStyle}>
              {countryStats.sum_activation_purchases || (countryStats.revenue_activation_count + countryStats.revenue_activation_purchase_count)}
            </td>
            <td style={cellStyle}>
              {(countryStats.cost || 0).toFixed(2)}
            </td>
            <td style={{
              ...cellStyle,
              backgroundColor: '#e9f7fe',
              fontWeight: 600
            }}>
              {getAdjustedRevenue(countryStats.total_revenue || 0, countryCode).toFixed(2)}
            </td>
            
            {/* P1-P12 values for country */}
            {Array.from({ length: 12 }, (_, i) => {
              const periodNum = i + 1;
              const periodKey = `revenue_p${periodNum}`;
              let value = 0;
              
              // Try to get period value from periods array if it exists
              if (Array.isArray(countryStats.periods)) {
                const periodData = countryStats.periods.find(p => p.period === periodNum);
                if (periodData) {
                  value = periodData.revenue;
                }
              }
              
              return (
                <td key={`country-${countryCode}-p${periodNum}`} style={cellStyle}>
                  {getAdjustedRevenue(value, countryCode).toFixed(2)}
                </td>
              );
            })}
            
            <td style={{
              ...cellStyle,
              backgroundColor: '#e9f7fe',
              fontWeight: 600
            }}>
              {getAdjustedRevenue(countryStats.total_revenue || 0, countryCode).toFixed(2)}
            </td>
            
            <td style={{
              ...cellStyle,
              backgroundColor: '#f0fff0',
              fontWeight: 600
            }}>
              {(countryStats.profit || getAdjustedRevenue(countryStats.total_revenue || 0, countryCode) - (countryStats.cost || 0)).toFixed(2)}
            </td>
            <td style={cellStyle}>
              {(countryStats.cpa || 0).toFixed(2)}
            </td>
            <td style={cellStyle}>
              {(countryStats.cpu || 0).toFixed(2)}
            </td>
            <td style={cellStyle}>
              {(countryStats.ltv || 0).toFixed(2)}
            </td>
            <td style={cellStyle}>
              {(countryStats.predicted_ltv || 0).toFixed(2)}
            </td>
            <td style={cellStyle}>
              {(countryStats.roi || 0).toFixed(2)}%
            </td>
            <td style={cellStyle}>
              {(countryStats.predicted_roi || 0).toFixed(2)}%
            </td>
          </tr>
        );
        
        // If country is expanded, add campaign rows for that country
        if (expandedCountries[countryCode] && showCampaigns) {
          // Get campaigns for this country
          const countryCampaigns = dashboardData.data.filter(item => item.country === countryCode);
          
          if (countryCampaigns.length > 0) {
            // For each campaign of this country
            countryCampaigns.forEach((campaign, index) => {
              tableRows.push(
                <tr 
                  key={`campaign-${countryCode}-${campaign.campaign}-${index}`} 
                  style={{
                    borderBottom: '1px solid #e9ecef',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => handleRowClick(campaign, `${campaign.campaign} (${countryCode})`, countryCode)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                >
                  <td style={campaignRowStyle}>
                    {campaign.campaign}
                  </td>
                  <td style={cellStyle}>
                    {campaign.revenue_activation_count || 0}
                  </td>
                  <td style={cellStyle}>
                    {campaign.revenue_activation_purchase_count || 0}
                  </td>
                  <td style={cellStyle}>
                    {campaign.sum_activation_purchases || (campaign.revenue_activation_count + campaign.revenue_activation_purchase_count)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.cost || 0).toFixed(2)}
                  </td>
                  <td style={{
                    ...cellStyle,
                    backgroundColor: '#e9f7fe'
                  }}>
                    {getAdjustedRevenue(campaign.total_revenue || 0, countryCode).toFixed(2)}
                  </td>
                  
                  {/* P1-P12 values for campaign */}
                  {Array.from({ length: 12 }, (_, i) => {
                    const periodNum = i + 1;
                    let value = 0;
                    
                    // Try to get period value from periods array if it exists
                    if (Array.isArray(campaign.periods)) {
                      const periodData = campaign.periods.find(p => p.period === periodNum);
                      if (periodData) {
                        value = periodData.revenue;
                      }
                    }
                    
                    return (
                      <td key={`campaign-${campaign.campaign}-p${periodNum}`} style={cellStyle}>
                        {getAdjustedRevenue(value, countryCode).toFixed(2)}
                      </td>
                    );
                  })}
                  
                  <td style={{
                    ...cellStyle,
                    backgroundColor: '#e9f7fe'
                  }}>
                    {getAdjustedRevenue(campaign.total_revenue || 0, countryCode).toFixed(2)}
                  </td>
                  
                  <td style={{
                    ...cellStyle,
                    backgroundColor: '#f0fff0'
                  }}>
                    {(campaign.profit || getAdjustedRevenue(campaign.total_revenue || 0, countryCode) - (campaign.cost || 0)).toFixed(2)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.cpa || 0).toFixed(2)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.cpu || 0).toFixed(2)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.ltv || 0).toFixed(2)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.predicted_ltv || 0).toFixed(2)}
                  </td>
                  <td style={cellStyle}>
                    {(campaign.roi || 0).toFixed(2)}%
                  </td>
                  <td style={cellStyle}>
                    {(campaign.predicted_roi || 0).toFixed(2)}%
                  </td>
                </tr>
              );
            });
          } else {
            // No campaigns found for this country
            tableRows.push(
              <tr key={`no-campaigns-${countryCode}`}>
                <td 
                  colSpan={26}
                  style={{
                    ...campaignRowStyle,
                    textAlign: 'center',
                    color: '#6c757d',
                    fontStyle: 'italic'
                  }}
                >
                  No campaigns found for {countryCode}
                </td>
              </tr>
            );
          }
        }
      });
    }
    
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        margin: '0 30px 30px 30px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
        overflow: 'auto'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f0f2f5' }}>
              <th style={{
                padding: '15px 20px',
                textAlign: 'left',
                fontWeight: 600,
                color: '#6c757d',
                fontSize: '13px',
                textTransform: 'uppercase',
                borderBottom: '2px solid #dee2e6',
                width: '25%'
              }}>ENTITY</th>
              <th style={{
                ...headerStyle
              }}>REV. ACT. COUNT</th>
              <th style={{
                ...headerStyle
              }}>REV. ACT. PURCH. COUNT</th>
              <th style={{
                ...headerStyle
              }}>SUM ACTIVATION</th>
              <th style={{
                ...headerStyle
              }}>COST</th>
              <th style={{
                ...headerStyle,
                backgroundColor: '#e9f7fe'
              }}>
                TOTAL REVENUE
                {showWithTax && (
                  <span style={{ 
                    fontSize: '10px', 
                    display: 'block', 
                    fontWeight: 'normal',
                    color: '#2c3e50',
                    marginTop: '2px' 
                  }}>
                    After VAT
                  </span>
                )}
              </th>
              
              {/* P1-P12 headers */}
              {Array.from({ length: 12 }, (_, i) => i + 1).map(period => (
                <th key={`header-p${period}`} style={headerStyle}>
                  P{period}
                  {showWithTax && (
                    <span style={{ 
                      fontSize: '10px', 
                      display: 'block', 
                      fontWeight: 'normal',
                      color: '#2c3e50',
                      marginTop: '2px' 
                    }}>
                      After VAT
                    </span>
                  )}
                </th>
              ))}
              
              <th style={{
                ...headerStyle,
                backgroundColor: '#e9f7fe'
              }}>
                TOTAL PREDICTED REVENUE
                {showWithTax && (
                  <span style={{ 
                    fontSize: '10px', 
                    display: 'block', 
                    fontWeight: 'normal',
                    color: '#2c3e50',
                    marginTop: '2px' 
                  }}>
                    After VAT
                  </span>
                )}
              </th>
              
              <th style={{
                ...headerStyle,
                backgroundColor: '#f0fff0'
              }}>PROFIT</th>
              <th style={{
                ...headerStyle
              }}>CPA</th>
              <th style={{
                ...headerStyle
              }}>CPU</th>
              <th style={{
                ...headerStyle
              }}>LTV</th>
              <th style={{
                ...headerStyle
              }}>PREDICTED LTV</th>
              <th style={{
                ...headerStyle
              }}>ROI (%)</th>
              <th style={{
                ...headerStyle
              }}>PREDICTED ROI (%)</th>
            </tr>
          </thead>
          <tbody>
            {tableRows}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Main render
  return (
    <div style={{ 
      width: '100%', 
      minHeight: '100vh',
      backgroundColor: '#f4f6f9'
    }}>
      <Header />
      {isFilterOpen && <Filters />}
      
      <CountryModal />
      <CampaignModal />
      <DetailModal />
      
      <div style={{ padding: '20px 0' }}>
        {loading ? (
          <Loader />
        ) : error ? (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#e74c3c',
            backgroundColor: 'white',
            borderRadius: '12px',
            margin: '0 30px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
          }}>
            {error}
          </div>
        ) : (
          <>
            <div>
              <h3 style={{ marginLeft: '30px', color: '#2c3e50' }}>
                {viewMode === 'daily' ? 'Daily' : 'Monthly'} Statistics {dashboardData?.period ? `(${dashboardData.period})` : ''}
              </h3>
              <StatisticsTable />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyStatsDashboard;